import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import { queryKeys } from "../lib/queryKeys";
import type { Contact, IcpFilterStatus, IcpScoreEntry } from "../lib/types";

export type ContactsSort = "newest" | "oldest" | "score_high" | "score_low";

export interface ContactsQuery {
  page: number; // 1-based
  pageSize: number;
  sort: ContactsSort;
  search: string;
  icpName: string | null;
  status: IcpFilterStatus;
  scoreMin: number | null;
  scoreMax: number | null;
}

export interface ContactsResult {
  contacts: Contact[];
  total: number;
}

// ---------------------------------------------------------------------------
// API wire types — mirror cloudFunctions/cf_fetch_contacts.py.
// ---------------------------------------------------------------------------

interface ApiIcpEntry {
  icp_name: string;
  score: number | null;
  rationale: string | null;
  breakdown: Record<string, number | string> | null;
  pushed: boolean;
  excluded_by: string | null;
  processed_at: string | null;
}

interface ApiContact {
  apollo_id: string;
  email: string | null;
  created_at: string | null;
  processed_at: string | null;
  name: string;
  title: string;
  company: string;
  domain: string;
  country: string;
  industry: string;
  icp_entries: ApiIcpEntry[];
}

interface ApiResponse {
  contacts: ApiContact[];
  total: number;
}

interface ApiRequest {
  limit: number;
  offset: number;
  sort: ContactsSort;
  status: IcpFilterStatus;
  icp_name?: string;
  score_min?: number;
  score_max?: number;
  search?: string;
}

const callContacts = httpsCallable<ApiRequest, ApiResponse>(
  functions,
  "cf_fetch_contacts",
);

function projectContact(api: ApiContact): Contact {
  const icpScores: Record<string, IcpScoreEntry> = {};
  for (const entry of api.icp_entries) {
    icpScores[entry.icp_name] = {
      score: entry.score,
      pushed: entry.pushed,
      rationale: entry.rationale,
      breakdown: entry.breakdown,
      excluded_by: entry.excluded_by,
      processed_at: entry.processed_at,
    };
  }
  return {
    id: api.apollo_id,
    name: api.name,
    title: api.title,
    email: api.email ?? "",
    company: api.company,
    domain: api.domain,
    country: api.country,
    industry: api.industry,
    icp_scores: icpScores,
    created_at: api.created_at,
    processed_at: api.processed_at,
  };
}

function buildRequest(q: ContactsQuery): ApiRequest {
  const req: ApiRequest = {
    limit: q.pageSize,
    offset: Math.max(0, (q.page - 1) * q.pageSize),
    sort: q.sort,
    status: q.status,
  };
  if (q.icpName) req.icp_name = q.icpName;
  if (q.scoreMin !== null) req.score_min = q.scoreMin;
  if (q.scoreMax !== null) req.score_max = q.scoreMax;
  const search = q.search.trim();
  if (search) req.search = search;
  return req;
}

async function fetchContacts(q: ContactsQuery): Promise<ContactsResult> {
  const res = await callContacts(buildRequest(q));
  return {
    contacts: res.data.contacts.map(projectContact),
    total: res.data.total,
  };
}

/**
 * Server-side contacts query — every filter/sort/page change triggers a new
 * fetch. The query key includes all params so React Query caches each
 * variant independently (e.g. switching back to a recent filter is instant).
 *
 * Pass *enabled* false to keep the query idle until the user clicks "Load
 * contacts" — matches the existing UX where the page doesn't auto-fetch.
 */
export function useContacts(query: ContactsQuery, enabled: boolean) {
  return useQuery<ContactsResult>({
    queryKey: queryKeys.contacts.list(query),
    queryFn: () => fetchContacts(query),
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev, // keep previous page visible while refetching
  });
}

/**
 * Debounce a fast-changing value. Useful for the search input so we don't
 * fire a fetch on every keystroke.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
